import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, PawPrint, Phone } from "lucide-react";
import { BUSINESS } from "@/data/site";
import { Button } from "@/components/ui/button";

const SECTIONS = [
  { id: "home", label: "Home" },
  { id: "about", label: "About" },
  { id: "services", label: "Services & Pricing" },
  { id: "care", label: "How We Care" },
  { id: "gallery", label: "Gallery" },
  { id: "faq", label: "FAQ" },
  { id: "contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToSection = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate(id === "home" ? "/" : `/#${id}`);
      return;
    }
    if (id === "home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const scrollToEl = (behavior) => {
      const el = document.getElementById(id);
      if (!el) return;
      const y = el.getBoundingClientRect().top + window.scrollY - 96;
      window.scrollTo({ top: y, behavior });
    };
    scrollToEl("smooth");
    // Correction pass: fixes any residual offset from images settling during the scroll.
    setTimeout(() => scrollToEl("smooth"), 450);
    setTimeout(() => scrollToEl("auto"), 900);
  };

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 backdrop-blur-xl bg-[#FDFBF7]/90 border-b transition-all duration-300 ${
        scrolled ? "border-border shadow-[0_4px_20px_rgba(136,156,133,0.08)]" : "border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <button onClick={() => goToSection("home")} data-testid="nav-logo" className="flex items-center gap-2.5 group shrink-0">
          <span className="w-11 h-11 rounded-full bg-primary flex items-center justify-center text-primary-foreground shadow-[0_6px_18px_rgba(136,156,133,0.35)] transition-transform duration-300 group-hover:-translate-y-0.5">
            <PawPrint className="w-6 h-6" />
          </span>
          <span className="leading-tight text-left">
            <span className="block font-heading text-xl sm:text-2xl font-semibold text-foreground whitespace-nowrap">{BUSINESS.name}</span>
            <span className="block text-xs text-muted-foreground">{BUSINESS.serviceArea}</span>
            <span className="block text-xs text-muted-foreground">Family Operated</span>
          </span>
        </button>

        <div className="hidden lg:flex items-center gap-1">
          {SECTIONS.map((n) => (
            <button
              key={n.id}
              onClick={() => goToSection(n.id)}
              data-testid={`nav-link-${n.id}`}
              className="px-3 py-2 rounded-full text-[15px] font-medium text-muted-foreground hover:text-foreground hover:bg-accent/60 transition-colors duration-200 whitespace-nowrap"
            >
              {n.label}
            </button>
          ))}
          <Link
            to="/merchandise"
            data-testid="nav-link-merchandise"
            className={`px-3 py-2 rounded-full text-[15px] font-medium transition-colors duration-200 whitespace-nowrap ${
              location.pathname === "/merchandise" ? "text-foreground bg-accent" : "text-muted-foreground hover:text-foreground hover:bg-accent/60"
            }`}
          >
            Merchandise
          </Link>
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <a href={`tel:${BUSINESS.phoneRaw}`} data-testid="nav-call" className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors whitespace-nowrap">
            <Phone className="w-4 h-4" /> {BUSINESS.phoneDisplay}
          </a>
          <Button onClick={() => goToSection("contact")} data-testid="nav-contact-btn" className="rounded-full px-6 h-11 text-[15px] transition-transform duration-300 hover:-translate-y-0.5">
            Contact Us
          </Button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden w-11 h-11 flex items-center justify-center rounded-full hover:bg-accent transition-colors"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#FDFBF7] border-t border-border px-5 py-4 space-y-1">
          {SECTIONS.map((n) => (
            <button key={n.id} onClick={() => goToSection(n.id)} className="block w-full text-left px-4 py-3 rounded-xl text-base font-medium text-muted-foreground hover:bg-accent/60 transition-colors">
              {n.label}
            </button>
          ))}
          <Link to="/merchandise" className="block px-4 py-3 rounded-xl text-base font-medium text-muted-foreground hover:bg-accent/60 transition-colors">Merchandise</Link>
          <div className="pt-3 flex flex-col gap-2">
            <Button onClick={() => goToSection("contact")} data-testid="mobile-contact-btn" className="rounded-full h-12 w-full">Contact Us</Button>
            <a href={`tel:${BUSINESS.phoneRaw}`} className="flex items-center justify-center gap-2 h-12 rounded-full border border-border text-foreground font-medium">
              <Phone className="w-4 h-4" /> Call {BUSINESS.phoneDisplay}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
